/**
 * Resolve an audit handoff action's `field` to editable OUTPUT field target(s).
 *
 * An `AuditHandoffAction.field` is a **short field name** (see the proto:
 * `string field = 1; // short field name, or "case" for case-level`) — not a path. So
 * `individual_deductible_amount` may actually live several levels down the output
 * schema (`benefits.deductible.individual_deductible_amount`) or inside an entity array
 * (`drug[0].coverage.copay`). To let the auditor edit the flagged field in place we have
 * to (a) FIND it in the schema and (b) show WHERE it lives, hence `parents`.
 *
 * Matching precedence — an exact full-path match wins outright, so a top-level `copay`
 * is never shadowed by `drug[0].coverage.copay`; only when nothing matches by path do we
 * fall back to leaf-id matches (which may legitimately return one target per entity
 * instance — the same note applies to each).
 *
 * PURE (no React/IO) — values are never read or returned here, only paths, so this module
 * never touches PHI.
 */
import type { FieldGroup, FormField } from '@/types/formSchema';

import { instanceLabel } from './humanCall/fieldTree';
import { formatFieldName } from './string';

/** The literal `field` value the auditor uses for a case-level (non-field) note. */
export const CASE_LEVEL_FIELD = 'case';

/**
 * True for an action that addresses the CASE rather than one field — `field: "case"`, or
 * no field at all. It carries a note and nothing else, so it renders with no field name
 * and can be edited as a free-form ("custom") handoff.
 */
export function isCaseLevelHandoff(action: { field?: string }): boolean {
  const field = (action.field ?? '').trim().toLowerCase();
  return field === '' || field === CASE_LEVEL_FIELD;
}

/**
 * The handoff actions worth SHOWING, ordered for display.
 *
 * Case-level actions (`field: "case"`, or no field) come FIRST — they frame the whole
 * case, so they read as the heading of the list, and every surface renders them without a
 * field name (see {@link handoffFieldLabel}). A case-level action with no note carries
 * nothing to show or edit, so it is dropped; field-level actions are always kept, their
 * field name being the point even when the auditor left no note.
 */
export function visibleHandoffActions<T extends { field?: string; note?: string }>(
  actions: T[],
): T[] {
  const shown = actions.filter(a => !isCaseLevelHandoff(a) || (a.note ?? '').trim() !== '');
  // Two stable passes rather than a sort — order within each group is the auditor's.
  return [...shown.filter(isCaseLevelHandoff), ...shown.filter(a => !isCaseLevelHandoff(a))];
}

/**
 * The handoff actions the CALLER should act on: the visible actions from the most recent
 * audit that left any.
 *
 * Audits arrive newest-first (`analyzed_at` DESC), and an audit whose actions are all
 * dropped by {@link visibleHandoffActions} — e.g. a lone case-level action with no note —
 * carries nothing to show, so it falls through to an older one rather than rendering an
 * empty panel. This stays a
 * pure pick so it can serve both the script tab and the Review view.
 */
export function latestHandoffActions<T extends { field?: string; note?: string }>(
  audits: Array<{ handoff_actions?: T[] }>,
): T[] {
  for (const audit of audits) {
    const actions = visibleHandoffActions(audit.handoff_actions ?? []);
    if (actions.length > 0) return actions;
  }
  return [];
}

/** One editable output field a handoff action points at. */
export interface HandoffFieldTarget {
  /** Concrete output path: `plan_name` | `benefits.copay` | `drug[0].coverage.copay`. */
  path: string;
  /** Schema def for the leaf — fed straight to `FormFieldRenderer`. */
  field: FormField;
  /**
   * Ancestor labels, outermost first, down to the leaf's immediate parent — an entity
   * element contributes one entry (`Drug · J1234`). Empty for a top-level field. Shown
   * as a breadcrumb so a nested leaf reads in context.
   */
  parents: string[];
}

/**
 * The nested template of a container field, or undefined when it has none — a
 * `fields`-less array is a repeated scalar/enum (a multi-value LEAF), not a sub-record.
 * Returning the children rather than a boolean keeps the descent branches below free of
 * unreachable `?? []` fallbacks.
 */
function templateOf(field: FormField): FormField[] | undefined {
  return field.fields && field.fields.length > 0 ? field.fields : undefined;
}

/**
 * Depth-first walk collecting every leaf under `field`, tagged with its concrete path
 * and ancestor labels. An entity array fans out one branch per element present in the
 * data; an EMPTY entity array still yields index 0 so a not-yet-captured field stays
 * editable (`setOutputAtPath` creates the element on write).
 */
function collectLeaves(
  field: FormField,
  path: string,
  valueAt: unknown,
  parents: string[],
  out: HandoffFieldTarget[],
): void {
  const children = templateOf(field);

  if (field.type === 'object' && children) {
    const child = (valueAt ?? {}) as Record<string, unknown>;
    const nextParents = [...parents, field.label];
    for (const f of children) {
      collectLeaves(f, `${path}.${f.id}`, child[f.id], nextParents, out);
    }
    return;
  }

  if (field.type === 'array' && children) {
    const raw = Array.isArray(valueAt) ? valueAt : [];
    const elements = raw.length > 0 ? raw : [{}];
    elements.forEach((element, index) => {
      const record = (element ?? {}) as Record<string, unknown>;
      const nextParents = [...parents, `${field.label} · ${instanceLabel(field, record, index)}`];
      for (const f of children) {
        collectLeaves(f, `${path}[${index}].${f.id}`, record[f.id], nextParents, out);
      }
    });
    return;
  }

  out.push({ path, field, parents });
}

/** Every leaf in the output schema, with concrete paths for the current data. */
function allOutputLeaves(
  groups: FieldGroup[],
  outputData: Record<string, unknown> | undefined,
): HandoffFieldTarget[] {
  const out: HandoffFieldTarget[] = [];
  for (const group of groups) {
    for (const field of group.fields ?? []) {
      collectLeaves(field, field.id, outputData?.[field.id], [], out);
    }
  }
  return out;
}

/**
 * Normalize dotted array indices to bracket form: `drug.0.coverage.x` →
 * `drug[0].coverage.x`. The schema walk always emits bracket paths (the form
 * `readRelativePath`/`setOutputAtPath` parse), but an authored `field` may use either.
 *
 * Exported because the staged-escalation keys have to agree with it: the field renderers
 * hand out dotted paths, an AI auditor's action may be authored either way, and two
 * spellings of one path must not become two escalation rows.
 */
export function normalizeHandoffFieldPath(path: string): string {
  return path.replace(/\.(\d+)(?=\.|$)/g, '[$1]');
}

/** Drop array indices entirely: `drug[0].coverage.x` → `drug.coverage.x`. */
function stripIndices(path: string): string {
  return path.replace(/\[\d+\]/g, '');
}

/** True when `candidate` is `path` or a SEGMENT-ALIGNED tail of it (never mid-segment). */
function isSegmentSuffix(path: string, candidate: string): boolean {
  return path === candidate || path.endsWith(`.${candidate}`);
}

/**
 * Resolve a handoff action's `field` to the output field target(s) it refers to.
 *
 * Returns an empty array for a case-level/blank `field`, or when the name matches
 * nothing in the schema — callers should then render the note without an editor rather
 * than guessing.
 */
export function resolveHandoffFieldTargets(
  groups: FieldGroup[],
  outputData: Record<string, unknown> | undefined,
  fieldName: string | undefined,
): HandoffFieldTarget[] {
  const raw = fieldName?.trim();
  if (!raw || raw.toLowerCase() === CASE_LEVEL_FIELD) return [];

  const name = normalizeHandoffFieldPath(raw);
  const leaves = allOutputLeaves(groups, outputData);

  // 1. An exact concrete path is unambiguous — take it and ignore same-named leaves
  //    elsewhere in the schema. Covers `drug[0].coverage.x` and `drug.0.coverage.x`.
  const exact = leaves.filter(l => l.path === name);
  if (exact.length > 0) return exact;

  // 2. Otherwise match on the INDEX-STRIPPED path as a segment-aligned tail, which in one
  //    rule covers a bare leaf id (`coverage_status`), a partial path
  //    (`coverage.coverage_status`), and an index-less entity path
  //    (`drug.coverage.coverage_status`). Matching a tail rather than the whole path is
  //    what makes the proto's "short field name" resolvable at all; it can legitimately
  //    hit several targets (the same leaf under multiple entity instances, or a name
  //    reused in two branches) and every hit is returned.
  const wanted = stripIndices(name).toLowerCase();
  return leaves.filter(l => isSegmentSuffix(stripIndices(l.path).toLowerCase(), wanted));
}

/** One step of a concrete output path: a field id, or an array index. */
interface PathToken {
  name?: string;
  index?: number;
}

/** Split `drug[1].coverage.copay` / `drug.1.coverage.copay` into ordered tokens. */
function tokenizeOutputPath(path: string): PathToken[] {
  const tokens: PathToken[] = [];
  const regex = /([^.[\]]+)|\[(\d+)\]/g;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(path)) !== null) {
    if (m[2] !== undefined) tokens.push({ index: Number(m[2]) });
    else if (/^\d+$/.test(m[1])) tokens.push({ index: Number(m[1]) });
    else tokens.push({ name: m[1] });
  }
  return tokens;
}

/** `Drug Details · J1234` for one element of an entity array, data present or not. */
function entityParentLabel(field: FormField, element: unknown, index: number): string {
  const record = (element ?? {}) as Record<string, unknown>;
  return `${field.label} · ${instanceLabel(field, record, index)}`;
}

/** State of the walk: where we are in the schema, in the data, and in the breadcrumb. */
interface WalkState {
  field: FormField;
  value: unknown;
  parents: string[];
  /** True once the current array field's element has been entered (index consumed). */
  inElement: boolean;
  /**
   * Position within a MULTI-VALUE leaf, when the path indexes into one. A `fields`-less
   * array (a repeated scalar/enum such as `pre_determination_submission_methods`) is a
   * leaf, but a flattened diff still addresses its elements — `…requirements[0]`. That
   * index belongs to the leaf, not to a container below it.
   */
  leafIndex?: number;
}

/** Enter one element of an entity array: name the instance, descend into its data. */
function enterElement(state: WalkState, index: number): void {
  const element = Array.isArray(state.value) ? state.value[index] : undefined;
  state.parents.push(entityParentLabel(state.field, element, index));
  state.value = element ?? {};
  state.inElement = true;
}

/** Descend into a named child of the current container. False when it has no such field. */
function stepIntoChild(state: WalkState, name: string, children: FormField[]): boolean {
  // A named step out of an entity array with no index of its own (`drug.coverage.copay`)
  // still crosses the element boundary — take the first instance, as the tail matcher does.
  if (state.field.type === 'array' && !state.inElement) enterElement(state, 0);
  else if (state.field.type === 'object') state.parents.push(state.field.label);

  const child = children.find(f => f.id === name);
  if (!child) return false;
  const parent = state.value as Record<string, unknown> | undefined;
  state.field = child;
  state.value = parent ? parent[child.id] : undefined;
  state.inElement = false;
  return true;
}

/** Consume one token, or return false when the schema cannot take that step. */
function stepOutputPath(state: WalkState, token: PathToken): boolean {
  const children = templateOf(state.field);
  if (!children) {
    // Indexing into a multi-value LEAF — the element belongs to this field, so stay on it.
    if (token.index === undefined) return false;
    state.leafIndex = token.index;
    return true;
  }
  if (token.index !== undefined) {
    enterElement(state, token.index);
    return true;
  }
  return stepIntoChild(state, token.name ?? '', children);
}

/** The first top-level schema field with this id, across all groups. */
function findRootField(groups: FieldGroup[], id: string): FormField | undefined {
  for (const group of groups) {
    for (const field of group.fields ?? []) {
      if (field.id === id) return field;
    }
  }
  return undefined;
}

/**
 * `parents › leaf label`, humanizing the leaf's id when the schema gives it no label and
 * marking WHICH element of a multi-value leaf the path addressed (the first is unmarked,
 * matching how raw leaf paths have always been humanized).
 */
function joinBreadcrumb(state: WalkState): string {
  const raw = state.field.label ? state.field.label.trim() : '';
  const label = raw || formatFieldName(state.field.id);
  const suffix = state.leafIndex && state.leafIndex > 0 ? ` #${state.leafIndex + 1}` : '';
  return [...state.parents, `${label}${suffix}`].join(BREADCRUMB_SEPARATOR);
}

/**
 * Humanized stand-in for a path segment the schema could not name. Index 0 is dropped
 * (the single-instance common case) and later indices read `#n`, matching how the audit
 * panel has always humanized raw leaf paths.
 */
function rawSegmentLabel(token: PathToken): string {
  if (token.index !== undefined) return token.index > 0 ? `#${token.index + 1}` : '';
  return formatFieldName(token.name ?? '');
}

/**
 * Breadcrumb for a CONCRETE output path, walked SEGMENT BY SEGMENT against the schema:
 * `drug[1].coverage.copay` → `Drug Details · J1234 › Drug Coverage › Copay`.
 *
 * Unlike {@link handoffFieldLabel} — which tail-MATCHES a short authored name against every
 * leaf and therefore always reports the first instance — this follows the path's own
 * indices, so `drug[1]` and `drug[4]` read as different drugs. That matters for audit
 * corrections, which the auditor emits one per entity instance
 * (`procedure.0.coverage.coinsurance` … `procedure.7.coverage.coinsurance`): tail matching
 * would label all eight as instance 0.
 *
 * An index with no element in `outputData` (the auditor corrected an instance the case no
 * longer has) falls back to the 1-based position, exactly as `instanceLabel` does.
 *
 * Returns '' when the path cannot be walked — the caller should then fall back to
 * {@link handoffFieldLabel}, which handles short names and partial paths.
 */
export function outputPathBreadcrumb(
  groups: FieldGroup[],
  outputData: Record<string, unknown> | undefined,
  path: string,
): string {
  const tokens = tokenizeOutputPath((path || '').trim());
  const rootName = tokens.length > 0 ? tokens[0].name : undefined;
  if (!rootName) return '';

  const root = findRootField(groups, rootName);
  if (!root) return '';

  const state: WalkState = {
    field: root,
    value: outputData ? outputData[root.id] : undefined,
    parents: [],
    inElement: false,
  };
  // Walk as far as the schema allows. A step can fail because the case's schema version no
  // longer declares that leaf (or never did) — in which case the ancestors already
  // resolved are still the most useful thing to show, above all the entity instance, so
  // the remaining raw segments are humanized onto the end rather than the whole breadcrumb
  // being thrown away. `stepIntoChild` pushes the container's own label before it looks
  // the child up, which is what keeps that ancestor in the list here.
  const rest = tokens.slice(1);
  for (let i = 0; i < rest.length; i++) {
    if (stepOutputPath(state, rest[i])) continue;
    const tail = rest.slice(i).map(rawSegmentLabel).filter(Boolean);
    // Nothing nameable left (a dropped index) — the field we reached IS the leaf, so name
    // it rather than ending the breadcrumb on its parent.
    if (tail.length === 0) return joinBreadcrumb(state);
    return [...state.parents, ...tail].join(BREADCRUMB_SEPARATOR);
  }
  return joinBreadcrumb(state);
}

/** Separator between breadcrumb segments in a handoff action's display label. */
const BREADCRUMB_SEPARATOR = ' › ';

/**
 * Human-readable display label for a handoff action's `field`, which the auditor writes
 * as a raw short field name (`drug[0].coverage.coverage_status`) — unhelpful to read.
 *
 * - **top-level field** → the schema label alone ("Plan Name").
 * - **nested field** → the full breadcrumb, outermost first, with an entity instance
 *   contributing its own segment: `drug[0].coverage.coverage_status` reads as
 *   `Drug Details · J2309 › Drug Coverage`. The raw name is NOT appended — the ancestor
 *   chain is what disambiguates a label like "Copay" that exists under several parents,
 *   and it does so in words the auditor recognises.
 * - **case-level or blank** → the empty string: a case-level action has no field to name,
 *   and every surface renders it with no label (see {@link isCaseLevelHandoff}).
 * - **unresolvable, or a field with no label** → the raw value unchanged, so nothing is
 *   ever silently dropped from the UI.
 *
 * Deliberately takes no output data: both the label and the ancestor chain come from the
 * schema, so the result is stable regardless of what the case has captured.
 */
export function handoffFieldLabel(groups: FieldGroup[], fieldName: string | undefined): string {
  const raw = fieldName?.trim() ?? '';
  if (isCaseLevelHandoff({ field: raw })) return '';
  const [target] = resolveHandoffFieldTargets(groups, undefined, fieldName);
  const label = target?.field.label?.trim();
  if (!label) return raw;
  return [...target.parents, label].join(BREADCRUMB_SEPARATOR);
}
